import React, { useEffect, useState } from "react";
import { Area, AreaChart, CartesianGrid, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { Activity, RefreshCw, X } from "lucide-react";
import api from "@/lib/api";
import { fmtCurrency } from "@/lib/format";

function todayKey() {
  return new Intl.DateTimeFormat("en-CA", { timeZone: "Asia/Kolkata" }).format(new Date());
}

function getDailyMtm(trades) {
  const dailyTrades = trades.filter((trade) => trade.trade_date === todayKey());
  const realized = dailyTrades
    .filter((trade) => trade.status !== "RUNNING")
    .reduce((total, trade) => total + Number(trade.total_pnl || 0), 0);
  const open = dailyTrades
    .filter((trade) => trade.status === "RUNNING")
    .reduce((total, trade) => total + trade.legs.reduce((sum, leg) => sum + Number(leg.pnl || 0), 0), 0);
  return { dailyTrades, realized, open, total: realized + open };
}

function buildDailySeries(trades) {
  const histories = trades.map((trade) => {
    const fallback = trade.status === "RUNNING"
      ? trade.legs.reduce((sum, leg) => sum + Number(leg.pnl || 0), 0)
      : Number(trade.total_pnl || 0);
    const history = trade.mtm_history?.length
      ? trade.mtm_history
      : [{ timestamp: trade.created_at, pnl: fallback }];
    return history
      .map((point) => ({ timestamp: new Date(point.timestamp).getTime(), pnl: Number(point.pnl || 0) }))
      .filter((point) => Number.isFinite(point.timestamp))
      .sort((a, b) => a.timestamp - b.timestamp);
  });
  const timestamps = [...new Set(histories.flatMap((history) => history.map((point) => point.timestamp)))].sort((a, b) => a - b);

  return timestamps.map((timestamp) => ({
    time: new Date(timestamp).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", second: "2-digit" }),
    timestamp,
    total: Number(histories.reduce((total, history) => {
      const point = history.filter((item) => item.timestamp <= timestamp).at(-1);
      return total + (point?.pnl || 0);
    }, 0).toFixed(2)),
  }));
}

function ChartTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  const point = payload[0].payload;
  return (
    <div className="border border-white/10 bg-[#171717] px-3 py-2 shadow-xl text-xs">
      <p className="text-slate-500 mb-1">{label}</p>
      <p className="font-mono text-blue-300">Total {fmtCurrency(point.total)}</p>
    </div>
  );
}

export default function MtmChart() {
  const [points, setPoints] = useState([]);
  const [summary, setSummary] = useState({ realized: 0, open: 0, total: 0 });
  const [lastUpdated, setLastUpdated] = useState(null);
  const [error, setError] = useState("");

  const refresh = async () => {
    try {
      const response = await api.get("/trades");
      const mtm = getDailyMtm(response.data);
      const now = new Date();
      setSummary(mtm);
      setPoints(buildDailySeries(mtm.dailyTrades));
      setLastUpdated(now);
      setError("");
    } catch {
      setError("Unable to update MTM data");
    }
  };

  useEffect(() => {
    refresh();
    const timer = setInterval(refresh, 1000);
    return () => clearInterval(timer);
  }, []);

  const current = points[points.length - 1];
  const chartMin = points.length ? Math.min(...points.map((point) => point.total), 0) : 0;
  const chartMax = points.length ? Math.max(...points.map((point) => point.total), 0) : 0;

  return (
    <main className="min-h-screen bg-[#0a0a0a] text-slate-100 p-4 sm:p-6" data-testid="mtm-chart-page">
      <div className="max-w-6xl mx-auto space-y-5">
        <header className="flex items-start justify-between gap-4 border-b border-white/10 pb-4">
          <div>
            <div className="flex items-center gap-2 text-blue-300 mb-1">
              <Activity className="w-4 h-4" />
              <span className="text-[10px] uppercase tracking-[0.2em]">Live monitor</span>
            </div>
            <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight">MTM Curve</h1>
            <p className="text-sm text-slate-400 mt-1">Aggregate mark-to-market performance across your trades.</p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button type="button" onClick={refresh} title="Refresh MTM" className="h-9 w-9 inline-flex items-center justify-center border border-white/10 bg-[#141414] text-slate-300 hover:bg-white/10">
              <RefreshCw className="w-4 h-4" />
            </button>
            <button type="button" onClick={() => window.close()} title="Close chart" className="h-9 w-9 inline-flex items-center justify-center border border-white/10 bg-[#141414] text-slate-300 hover:bg-white/10">
              <X className="w-4 h-4" />
            </button>
          </div>
        </header>

        <section className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div className="border border-white/10 bg-[#141414] p-3">
            <p className="text-[10px] uppercase tracking-widest text-slate-500">Daily total P&amp;L / MTM</p>
            <p className={`font-mono text-xl mt-1 ${summary.total >= 0 ? "text-emerald-400" : "text-rose-400"}`}>{fmtCurrency(summary.total)}</p>
          </div>
          <div className="border border-white/10 bg-[#141414] p-3">
            <p className="text-[10px] uppercase tracking-widest text-slate-500">Daily open MTM</p>
            <p className="font-mono text-xl mt-1 text-amber-300">{fmtCurrency(summary.open)}</p>
          </div>
          <div className="border border-white/10 bg-[#141414] p-3">
            <p className="text-[10px] uppercase tracking-widest text-slate-500">Daily realized P&amp;L</p>
            <p className="font-mono text-xl mt-1 text-emerald-400">{fmtCurrency(summary.realized)}</p>
          </div>
        </section>

        <section className="border border-white/10 bg-[#141414] p-3 sm:p-5">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h2 className="font-heading font-semibold">Intraday performance</h2>
              <p className="text-xs text-slate-500 mt-1">Today&apos;s trades, from entry through the latest or final MTM.</p>
            </div>
            <span className="text-[10px] font-mono text-slate-500">{lastUpdated ? `Updated ${lastUpdated.toLocaleTimeString("en-IN")}` : "Connecting..."}</span>
          </div>
          <div className="h-[360px] sm:h-[480px]" data-testid="mtm-chart">
            {points.length < 1 ? (
              <div className="h-full flex items-center justify-center text-sm text-slate-500">No MTM data for today.</div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={points} margin={{ top: 8, right: 8, left: 8, bottom: 4 }}>
                  <defs>
                    <linearGradient id="mtmFill" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#38bdf8" stopOpacity={0.3} />
                      <stop offset="100%" stopColor="#38bdf8" stopOpacity={0.02} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid stroke="rgba(255,255,255,0.07)" vertical={false} />
                  <XAxis dataKey="time" tick={{ fill: "#64748b", fontSize: 10 }} minTickGap={36} tickLine={false} axisLine={false} />
                  <YAxis domain={[chartMin, chartMax]} tick={{ fill: "#64748b", fontSize: 10 }} tickFormatter={(value) => `₹${Math.round(value).toLocaleString("en-IN")}`} tickLine={false} axisLine={false} width={72} />
                  <Tooltip content={<ChartTooltip />} />
                  <ReferenceLine y={0} stroke="rgba(255,255,255,0.25)" />
                  <Area type="monotone" dataKey="total" stroke="#38bdf8" strokeWidth={2} fill="url(#mtmFill)" dot={false} isAnimationActive={false} />
                </AreaChart>
              </ResponsiveContainer>
            )}
          </div>
          {error && <p className="text-xs text-rose-400 mt-2">{error}</p>}
        </section>
      </div>
    </main>
  );
}
